import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import ScreenshotLangPicker from "../../components/ScreenshotLangPicker";
import {
    SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL,
    WORD_WHEEL_QUEST_GOOGLE_PLAY_URL,
} from "../../config/nativeAppsVisibility";
import { appendScreenshotLang, useScreenshotTranslation } from "../../lib/screenshotI18n";
import {
    APP_SITE_ID,
    resolvePublicAppSupportUrl,
    resolvePublicLegalUrl,
    resolvePublicMarketingUrl,
    JIGSAW_MUSE_EMAIL_ICON_URL,
    WORD_WHEEL_QUEST_EMAIL_ICON_SRC,
    SLIDING_PUZZLE_QUEST_EMAIL_ICON_SRC,
} from "../../config/appSiteLinks";
import { resolvePublicDataDeletionLinksForSiteApp } from "../../pages/login/data-deletion/dataDeletionRequestRoutes";
import slidingPuzzleQuestIcon from "../../assets/app-icons/sliding-puzzle-quest-app-icon.png";
import wordWheelQuestIcon from "../../assets/app-icons/word-wheel-quest-app-icon.png";
import wordSearchMiniIcon from "../../assets/app-icons/word-search-mini-app-icon.png";
import {
    APP_ICON_PATH as JIGSAW_APP_ICON_PATH,
    FEATURE_GRAPHIC_PATH as JIGSAW_FEATURE_GRAPHIC_PATH,
    JIGSAW_MOBILE_TITLE,
    MAIN_PATH as JIGSAW_MAIN_PATH,
    RECORDING_APPLE_CONTENT_PATH as JIGSAW_APPLE_CONTENT_PATH,
    RECORDING_COLOR_BASKET_FEATURE_PATH as JIGSAW_1080P_COLOR_BASKET_FEATURE_PATH,
    RECORDING_COLOR_BASKET_PATH as JIGSAW_1080P_COLOR_BASKET_PATH,
    RECORDING_CONTENT_PATH as JIGSAW_1080P_CONTENT_PATH,
    RECORDING_ENDING_PATH as JIGSAW_1080P_ENDING_PATH,
} from "../../prototype/mobile-jigsaw/routes";
import { RECORDING_FRAME_APPLE } from "../../prototype/mobile-jigsaw/recordingCaptureFrame";
import {
    APP_ICON_2_PATH as WORD_WHEEL_APP_ICON_2_PATH,
    APP_ICON_3_PATH as WORD_WHEEL_APP_ICON_3_PATH,
    APP_ICON_PATH as WORD_WHEEL_APP_ICON_PATH,
    FEATURE_GRAPHIC_PATH as WORD_WHEEL_FEATURE_GRAPHIC_PATH,
    MAIN_PATH,
    RECORDING_1080P_PATH,
    RECORDING_1080P_PACKS_PATH,
    RECORDING_ENDING_PATH as WORD_WHEEL_1080P_ENDING_PATH,
    WORD_WHEEL_QUEST_TITLE,
} from "../../prototype/mobile-word-wheel/routes";
import {
    VIEW_PHONE,
    VIEW_TABLET_LANDSCAPE,
    WORD_WHEEL_VIEW_WINDOWS,
    withWordWheelView,
} from "../../prototype/mobile-word-wheel/lib/viewMode";
import { formatMobileSlidingDraftValidThroughLabel } from "../../prototype/mobile-sliding/lib/draftAccess";
import {
    APP_ICON_3_PATH as SLIDING_APP_ICON_3_PATH,
    APP_ICON_FACEBOOK_PATH as SLIDING_APP_ICON_FACEBOOK_PATH,
    FEATURE_GRAPHIC_PATH as SLIDING_FEATURE_GRAPHIC_PATH,
    FEATURE_GRAPHIC_INSTAGRAM_PATH as SLIDING_FEATURE_GRAPHIC_INSTAGRAM_PATH,
    MAIN_PATH as SLIDING_MAIN_PATH,
    PLAY_PATH as SLIDING_PLAY_PATH,
    RECORDING_ENDING_PATH as SLIDING_1080P_ENDING_PATH,
    RECORDING_FEATURE_GRAPHIC_PATH as SLIDING_1080P_FEATURE_GRAPHIC_PATH,
    SLIDING_PUZZLE_QUEST_TITLE,
} from "../../prototype/mobile-sliding/routes";
import { CONTENT_RECORDING_SLIDING_ID } from "../../prototype/android/sliding-puzzle-quest/lib/contentRecording";
import { tabletIntroPath } from "../../prototype/tablet-intro/routes";
import { openBrowserPopup, openCapturePagePopup } from "../welcome/openBrowserPopup";
import { ADMIN_ADVERTISEMENT_PRODUCT_ICON_PATH } from "../advertisement/advertisementRoutes";

const SLIDING_1080P_CONTENT_PATH = `${SLIDING_PLAY_PATH}?slidingId=${CONTENT_RECORDING_SLIDING_ID}&recording=1080p`;
const SLIDING_1080P_CONTENT_6X6_HARD_PATH = `${SLIDING_PLAY_PATH}?recording=1080p&board=6x6-hard`;
const SELECTED_APP_STORAGE_KEY = "prototype.selectedAppTab";
const WORD_SEARCH_MINI_APP_ID = "word_search_mini";

const APP_TABS = [
    {
        title: SLIDING_PUZZLE_QUEST_TITLE,
        path: SLIDING_MAIN_PATH,
        appId: APP_SITE_ID.SLIDING_PUZZLE_QUEST,
    },
    {
        title: JIGSAW_MOBILE_TITLE,
        path: JIGSAW_MAIN_PATH,
        appId: APP_SITE_ID.JIGSAW_MUSE,
    },
    {
        title: WORD_WHEEL_QUEST_TITLE,
        path: MAIN_PATH,
        appId: APP_SITE_ID.WORD_WHEEL_QUEST,
    },
    {
        title: "Word Search Mini",
        path: null,
        appId: WORD_SEARCH_MINI_APP_ID,
    },
];

const APP_STORE_ASSETS = {
    [APP_SITE_ID.SLIDING_PUZZLE_QUEST]: [
        {
            label: "App icon — (512×512)",
            path: SLIDING_APP_ICON_3_PATH,
            windowName: "puzzle-sliding-app-icon",
            caption: "512×512 · 2×2 glass tiles, bottom-right tile lifted 10°",
        },
        {
            label: "App icon — email (64×64)",
            fileHref: SLIDING_PUZZLE_QUEST_EMAIL_ICON_SRC,
            downloadName: "sliding-puzzle-quest-app-icon-64x64.png",
            previewSrc: SLIDING_PUZZLE_QUEST_EMAIL_ICON_SRC,
            caption: "64×64 · from store 512 icon · for emails",
        },
        {
            label: "App icon 2 (512×512) — Facebook",
            path: SLIDING_APP_ICON_FACEBOOK_PATH,
            windowName: "puzzle-sliding-app-icon-facebook",
            caption: "512×512 · same tiles, transparent background (Facebook)",
        },
        {
            label: "Feature graphic — Google Play (1024×500)",
            path: SLIDING_FEATURE_GRAPHIC_PATH,
            windowName: "puzzle-sliding-feature-graphic",
            caption: "1024×500 · screenshot/05 puzzle",
        },
        {
            label: "Feature graphic — Instagram (1080×1080)",
            path: SLIDING_FEATURE_GRAPHIC_INSTAGRAM_PATH,
            windowName: "puzzle-sliding-feature-graphic-instagram",
            caption: "1080×1080 · title top, puzzle center, CTAs bottom",
            popupWidth: 1120,
            popupHeight: 1200,
        },
    ],
    [APP_SITE_ID.JIGSAW_MUSE]: [
        {
            label: "App icon — (512×512)",
            path: JIGSAW_APP_ICON_PATH,
            windowName: "puzzle-jigsaw-app-icon",
            caption: "512×512 · golden M jigsaw piece",
        },
        {
            label: "App icon — email (64×64)",
            fileHref: JIGSAW_MUSE_EMAIL_ICON_URL,
            downloadName: "jigsaw-muse-app-icon-64x64.png",
            previewSrc: JIGSAW_MUSE_EMAIL_ICON_URL,
            caption: "64×64 · S3 logos/apps · used in review emails",
        },
        {
            label: "Feature graphic — Google Play (1024×500)",
            path: JIGSAW_FEATURE_GRAPHIC_PATH,
            windowName: "puzzle-jigsaw-feature-graphic",
            caption: "1024×500 · screenshot/05 Ocean",
        },
    ],
    [APP_SITE_ID.WORD_WHEEL_QUEST]: [
        {
            label: "App icon — Apple / Play (512×512)",
            path: WORD_WHEEL_APP_ICON_PATH,
            windowName: "puzzle-word-wheel-app-icon",
            caption: "512×512 · from android-word-wheel-quest icon.png",
            previewSrc: `${process.env.PUBLIC_URL || ""}/ad/word-wheel-quest-app-icon-512.png`,
        },
        {
            label: "App icon — email (64×64)",
            fileHref: WORD_WHEEL_QUEST_EMAIL_ICON_SRC,
            downloadName: "word-wheel-quest-app-icon-64x64.png",
            previewSrc: WORD_WHEEL_QUEST_EMAIL_ICON_SRC,
            caption: "64×64 · from Apple / Play 512 icon · for emails",
        },
        {
            label: "App icon #2 — (512×512)",
            path: WORD_WHEEL_APP_ICON_2_PATH,
            windowName: "puzzle-word-wheel-app-icon-2",
            caption: "512×512 · independent copy for updates",
            previewSrc: `${process.env.PUBLIC_URL || ""}/ad/word-wheel-quest-app-icon-2-512.png`,
        },
        {
            label: "App icon #3 — (512×512)",
            path: WORD_WHEEL_APP_ICON_3_PATH,
            windowName: "puzzle-word-wheel-app-icon-3",
            caption: "512×512 · company logo background, linked W-O-R-D",
        },
        {
            label: "Feature graphic — Google Play (1024×500)",
            path: WORD_WHEEL_FEATURE_GRAPHIC_PATH,
            windowName: "puzzle-word-wheel-feature-graphic",
            caption: "1024×500 · screenshot/05 puzzle",
        },
    ],
    [WORD_SEARCH_MINI_APP_ID]: [
        {
            label: "App icon — (512×512)",
            fileHref: `${process.env.PUBLIC_URL || ""}/ad/word-search-mini-app-icon-512.png`,
            downloadName: "word-search-mini-app-icon-512.png",
            previewSrc: `${process.env.PUBLIC_URL || ""}/ad/word-search-mini-app-icon-512.png`,
            caption: "512×512 · WARM grid with cyan/orange X highlighters",
        },
        {
            label: "App icon — email (64×64)",
            fileHref: `${process.env.PUBLIC_URL || ""}/ad/word-search-mini-app-icon-64.png`,
            downloadName: "word-search-mini-app-icon-64x64.png",
            previewSrc: `${process.env.PUBLIC_URL || ""}/ad/word-search-mini-app-icon-64.png`,
            caption: "64×64 · from store 512 icon · for emails",
        },
    ],
};

function readSelectedAppTab() {
    if (typeof window === "undefined") return 0;
    try {
        const saved = Number.parseInt(
            window.localStorage.getItem(SELECTED_APP_STORAGE_KEY),
            10,
        );
        return Number.isInteger(saved) && saved >= 0 && saved < APP_TABS.length
            ? saved
            : 0;
    } catch {
        return 0;
    }
}

function AppStoreAssetLinks({ appId, lang }) {
    const assets = APP_STORE_ASSETS[appId] || [];

    return (
        <Box sx={{ mt: 3, border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                App icon &amp; feature graphic
            </Typography>
            <ScreenshotLangPicker sx={{ mb: 1.5 }} />
            <Stack spacing={1.25}>
                {assets.map((asset) => {
                    const isFile = Boolean(asset.fileHref);
                    const localizedPath = isFile
                        ? asset.fileHref
                        : appendScreenshotLang(asset.path, lang);
                    const href = isFile
                        ? asset.fileHref
                        : typeof window === "undefined"
                            ? localizedPath
                            : `${window.location.origin}${localizedPath}`;
                    const openCapture = (event) => {
                        if (isFile) return;
                        event.preventDefault();
                        openCapturePagePopup(
                            href,
                            asset.windowName,
                            asset.popupWidth,
                            asset.popupHeight,
                        );
                    };
                    return (
                        <Box key={asset.path || asset.fileHref}>
                            <Link
                                href={href}
                                download={isFile ? asset.downloadName : undefined}
                                onClick={isFile ? undefined : openCapture}
                                sx={{ cursor: "pointer", fontWeight: 500 }}
                            >
                                {asset.label}
                            </Link>
                            {asset.previewSrc ? (
                                <Box
                                    component="img"
                                    src={asset.previewSrc}
                                    alt=""
                                    onClick={() => {
                                        if (isFile) {
                                            if (typeof window !== "undefined") {
                                                window.open(href, "_blank", "noopener,noreferrer");
                                            }
                                            return;
                                        }
                                        openCapturePagePopup(
                                            href,
                                            asset.windowName,
                                            asset.popupWidth,
                                            asset.popupHeight,
                                        );
                                    }}
                                    sx={{
                                        display: "block",
                                        width: 72,
                                        height: 72,
                                        mt: 0.75,
                                        objectFit: asset.previewFit || "cover",
                                        bgcolor: asset.previewFit === "contain" ? "grey.100" : undefined,
                                        borderRadius: 1.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        cursor: "pointer",
                                    }}
                                />
                            ) : null}
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.25 }}
                            >
                                {localizedPath} · {asset.caption}
                            </Typography>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

function PublicLinkRow({ label, url }) {
    if (!url) return null;
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.35 }}>
                {label}
            </Typography>
            <Link href={url} target="_blank" rel="noopener noreferrer">
                {url}
            </Link>
        </Box>
    );
}

function PublicLinks({ appId }) {
    const supportUrl = resolvePublicAppSupportUrl(appId);
    const marketingUrl = resolvePublicMarketingUrl(appId);
    const privacyUrl = resolvePublicLegalUrl(appId, "privacy");
    const termsUrl = resolvePublicLegalUrl(appId, "terms");
    const deletionLinks = resolvePublicDataDeletionLinksForSiteApp(appId);

    return (
        <Box sx={{ mt: 3, border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Public links
            </Typography>
            <Stack spacing={1.5}>
                <PublicLinkRow label="Support URL (App Store)" url={supportUrl} />
                <PublicLinkRow label="Marketing URL (App Store)" url={marketingUrl} />
                <PublicLinkRow label="Privacy Policy URL" url={privacyUrl} />
                <PublicLinkRow label="Terms of Service URL" url={termsUrl} />
                {deletionLinks.map(({ platform, label, url }) => (
                    <PublicLinkRow key={platform} label={label} url={url} />
                ))}
            </Stack>
        </Box>
    );
}

function openWordWheelView(view) {
    if (typeof window === "undefined") return;
    const cfg = WORD_WHEEL_VIEW_WINDOWS[view] || WORD_WHEEL_VIEW_WINDOWS[VIEW_PHONE];
    // 1080P CONTENT → dedicated Android-matching capture play page (not web play / home).
    const path =
        view === VIEW_TABLET_LANDSCAPE
            ? RECORDING_1080P_PATH
            : withWordWheelView(MAIN_PATH, view);
    const href = `${window.location.origin}${path}`;
    openBrowserPopup(href, `puzzle-word-wheel-${view}`, cfg.width, cfg.height);
}

function openSliding1080pContent() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${SLIDING_1080P_CONTENT_PATH}`;
    openBrowserPopup(href, "puzzle-sliding-1080p-content", 1920, 1080);
}

function openSliding1080pContent6x6Hard() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${SLIDING_1080P_CONTENT_6X6_HARD_PATH}`;
    openBrowserPopup(href, "puzzle-sliding-1080p-content-6x6-hard", 1920, 1080);
}

function openSliding1080pEnding() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${SLIDING_1080P_ENDING_PATH}`;
    openBrowserPopup(href, "puzzle-sliding-1080p-ending", 1920, 1080);
}

function openSliding1080pFeatureGraphic(lang) {
    if (typeof window === "undefined") return;
    const path = appendScreenshotLang(SLIDING_1080P_FEATURE_GRAPHIC_PATH, lang);
    const href = `${window.location.origin}${path}`;
    openBrowserPopup(href, "puzzle-sliding-1080p-feature-graphic", 1920, 1080);
}

function openJigsaw1080pContent() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${JIGSAW_1080P_CONTENT_PATH}`;
    openBrowserPopup(href, "puzzle-jigsaw-1080p-content", 1920, 1080);
}

function openJigsaw1080pColorBasket() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${JIGSAW_1080P_COLOR_BASKET_PATH}`;
    openBrowserPopup(href, "puzzle-jigsaw-1080p-color-basket", 1920, 1080);
}

function openJigsaw1080pColorBasketFeature() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${JIGSAW_1080P_COLOR_BASKET_FEATURE_PATH}`;
    openBrowserPopup(href, "puzzle-jigsaw-1080p-color-basket-feature", 1920, 1080);
}

function openJigsawAppleContent() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${JIGSAW_APPLE_CONTENT_PATH}`;
    openBrowserPopup(
        href,
        "puzzle-jigsaw-apple-content",
        RECORDING_FRAME_APPLE.width,
        RECORDING_FRAME_APPLE.height,
    );
}

function openJigsaw1080pEnding() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${JIGSAW_1080P_ENDING_PATH}`;
    openBrowserPopup(href, "puzzle-jigsaw-1080p-ending", 1920, 1080);
}

function openWordWheel1080pPacks() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${RECORDING_1080P_PACKS_PATH}`;
    openBrowserPopup(href, "puzzle-word-wheel-1080p-packs", 1920, 1080);
}

function openWordWheel1080pEnding() {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${WORD_WHEEL_1080P_ENDING_PATH}`;
    openBrowserPopup(href, "puzzle-word-wheel-1080p-ending", 1920, 1080);
}

function openTabletIntro(appId) {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${tabletIntroPath(appId)}`;
    openBrowserPopup(href, `puzzle-1080p-intro-${appId}`, 1920, 1080);
}

function openAppleIntro(appId) {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${tabletIntroPath(appId)}`;
    openBrowserPopup(
        href,
        `puzzle-apple-intro-${appId}`,
        RECORDING_FRAME_APPLE.width,
        RECORDING_FRAME_APPLE.height,
    );
}

export default function PrototypeMain() {
    const navigate = useNavigate();
    const { lang } = useScreenshotTranslation();
    const [selectedAppTab, setSelectedAppTab] = useState(readSelectedAppTab);
    const selectedApp = APP_TABS[selectedAppTab];
    const isWordWheel = selectedApp.appId === APP_SITE_ID.WORD_WHEEL_QUEST;

    useEffect(() => {
        try {
            window.localStorage.setItem(
                SELECTED_APP_STORAGE_KEY,
                String(selectedAppTab),
            );
        } catch {
            // Local storage can be unavailable in private or restricted contexts.
        }
    }, [selectedAppTab]);

    return (
        <Box sx={{ p: 2, width: "100%", maxWidth: 920 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Prototype — For recording purpose
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Draft mobile experiences and experimental UI — not linked from the public site.
            </Typography>
            <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600, mb: 3 }}>
                Available through {formatMobileSlidingDraftValidThroughLabel()}.
            </Typography>

            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                <Tabs
                    value={selectedAppTab}
                    onChange={(_, nextTab) => setSelectedAppTab(nextTab)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    aria-label="Prototype applications"
                    sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        "& .MuiTab-root": {
                            minHeight: 52,
                            fontWeight: 600,
                            textTransform: "none",
                        },
                    }}
                >
                    {APP_TABS.map(({ title, appId }) => (
                        <Tab
                            key={title}
                            label={title}
                            icon={appId === WORD_SEARCH_MINI_APP_ID ? (
                                <Box
                                    component="img"
                                    src={wordSearchMiniIcon}
                                    alt=""
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "6px",
                                        objectFit: "cover",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                                    }}
                                />
                            ) : undefined}
                            iconPosition="start"
                        />
                    ))}
                </Tabs>

                <Box
                    role="tabpanel"
                    aria-label={selectedApp.title}
                    sx={{ p: { xs: 2, sm: 2.5 } }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {selectedApp.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {selectedApp.appId === WORD_SEARCH_MINI_APP_ID
                            ? "App icon is ready (512×512). Feature graphic and capture pages can be added later."
                            : isWordWheel
                            ? "Open phone or 1920×1080 intro/content capture windows for the web prototype."
                            : selectedApp.appId === APP_SITE_ID.SLIDING_PUZZLE_QUEST
                              ? "Open the web prototype here, or a 1920×1080 recording window for capture."
                              : "Open the web prototype here, Google 1920×1080 or Apple 1290×2796 capture windows."}
                    </Typography>

                    {selectedApp.appId === WORD_SEARCH_MINI_APP_ID ? (
                        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
                            <Box
                                component="img"
                                src={wordSearchMiniIcon}
                                alt="Word Search Mini app icon"
                                sx={{
                                    width: 96,
                                    height: 96,
                                    borderRadius: "20px",
                                    objectFit: "cover",
                                    boxShadow: "0 2px 10px rgba(0,0,0,0.16)",
                                    border: 1,
                                    borderColor: "divider",
                                }}
                            />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Word Search Mini
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Store icon 512×512 · download below
                                </Typography>
                            </Box>
                        </Box>
                    ) : null}

                    {selectedApp.appId === WORD_SEARCH_MINI_APP_ID ? null : isWordWheel ? (
                        <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                            <Button
                                variant="contained"
                                onClick={() => openWordWheelView(VIEW_PHONE)}
                            >
                                Phone version
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => openTabletIntro(selectedApp.appId)}
                            >
                                1080P - INTRO
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => openWordWheelView(VIEW_TABLET_LANDSCAPE)}
                            >
                                1080P CONTENT
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={openWordWheel1080pPacks}
                            >
                                1080P - Packs
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={openWordWheel1080pEnding}
                            >
                                1080P - ENDING
                            </Button>
                        </Stack>
                    ) : selectedApp.appId === APP_SITE_ID.SLIDING_PUZZLE_QUEST ? (
                        <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                            <Button
                                variant="contained"
                                onClick={() => navigate(selectedApp.path)}
                            >
                                Open web prototype
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => openTabletIntro(selectedApp.appId)}
                            >
                                1080P - INTRO
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={openSliding1080pContent}
                            >
                                1080P(content)
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={openSliding1080pContent6x6Hard}
                            >
                                1080P(content) - 6x6 hard
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => openSliding1080pFeatureGraphic(lang)}
                            >
                                1080P - Featured graphic
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={openSliding1080pEnding}
                            >
                                1080P - ENDING
                            </Button>
                        </Stack>
                    ) : (
                        <Stack spacing={1.25}>
                            <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" alignItems="flex-start">
                                <Button
                                    variant="contained"
                                    onClick={() => navigate(selectedApp.path)}
                                >
                                    Open {selectedApp.title}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => openTabletIntro(selectedApp.appId)}
                                >
                                    1080P - INTRO
                                </Button>
                                <Stack spacing={0.5} alignItems="flex-start">
                                    <Button
                                        variant="outlined"
                                        onClick={openJigsaw1080pContent}
                                    >
                                        1080P - CONTENT
                                    </Button>
                                    <Link
                                        component={RouterLink}
                                        to={JIGSAW_1080P_CONTENT_PATH}
                                        variant="caption"
                                        sx={{ px: 0.5 }}
                                    >
                                        navigate
                                    </Link>
                                </Stack>
                                <Stack spacing={0.5} alignItems="flex-start">
                                    <Button
                                        variant="outlined"
                                        onClick={openJigsaw1080pColorBasket}
                                    >
                                        1080P - COLOR BASKET
                                    </Button>
                                    <Link
                                        component={RouterLink}
                                        to={JIGSAW_1080P_COLOR_BASKET_PATH}
                                        variant="caption"
                                        sx={{ px: 0.5 }}
                                    >
                                        navigate
                                    </Link>
                                </Stack>
                                <Stack spacing={0.5} alignItems="flex-start">
                                    <Button
                                        variant="outlined"
                                        onClick={openJigsaw1080pColorBasketFeature}
                                    >
                                        1080P - COLOR BASKET FEATURE
                                    </Button>
                                    <Link
                                        component={RouterLink}
                                        to={JIGSAW_1080P_COLOR_BASKET_FEATURE_PATH}
                                        variant="caption"
                                        sx={{ px: 0.5 }}
                                    >
                                        navigate
                                    </Link>
                                </Stack>
                                <Button
                                    variant="outlined"
                                    onClick={openJigsaw1080pEnding}
                                >
                                    1080P - ENDING
                                </Button>
                            </Stack>
                            <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" alignItems="flex-start">
                                <Button
                                    variant="outlined"
                                    onClick={() => openAppleIntro(selectedApp.appId)}
                                >
                                    APPLE - INTRO
                                </Button>
                                <Stack spacing={0.5} alignItems="flex-start">
                                    <Button
                                        variant="outlined"
                                        onClick={openJigsawAppleContent}
                                    >
                                        APPLE - CONTENT
                                    </Button>
                                    <Link
                                        component={RouterLink}
                                        to={JIGSAW_APPLE_CONTENT_PATH}
                                        variant="caption"
                                        sx={{ px: 0.5 }}
                                    >
                                        navigate
                                    </Link>
                                </Stack>
                            </Stack>
                        </Stack>
                    )}

                    {APP_STORE_ASSETS[selectedApp.appId] ? (
                        <AppStoreAssetLinks appId={selectedApp.appId} lang={lang} />
                    ) : null}

                    {selectedApp.appId === APP_SITE_ID.SLIDING_PUZZLE_QUEST
                    || selectedApp.appId === APP_SITE_ID.WORD_WHEEL_QUEST ? (
                        <Box sx={{ mt: 3, border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 2.5,
                                }}
                            >
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                                        Google Play Console
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box
                                                component="img"
                                                src={
                                                    selectedApp.appId === APP_SITE_ID.WORD_WHEEL_QUEST
                                                        ? wordWheelQuestIcon
                                                        : slidingPuzzleQuestIcon
                                                }
                                                alt={
                                                    selectedApp.appId === APP_SITE_ID.WORD_WHEEL_QUEST
                                                        ? WORD_WHEEL_QUEST_TITLE
                                                        : SLIDING_PUZZLE_QUEST_TITLE
                                                }
                                                title={
                                                    selectedApp.appId === APP_SITE_ID.WORD_WHEEL_QUEST
                                                        ? WORD_WHEEL_QUEST_TITLE
                                                        : SLIDING_PUZZLE_QUEST_TITLE
                                                }
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: "12px",
                                                    objectFit: "cover",
                                                    flexShrink: 0,
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.16)",
                                                    border: 1,
                                                    borderColor: "divider",
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {selectedApp.appId === APP_SITE_ID.WORD_WHEEL_QUEST
                                                    ? WORD_WHEEL_QUEST_TITLE
                                                    : SLIDING_PUZZLE_QUEST_TITLE}
                                            </Typography>
                                        </Stack>
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block", mb: 0.35 }}
                                            >
                                                Store listing
                                            </Typography>
                                            <Link
                                                href={
                                                    selectedApp.appId === APP_SITE_ID.WORD_WHEEL_QUEST
                                                        ? WORD_WHEEL_QUEST_GOOGLE_PLAY_URL
                                                        : SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Open Google Play
                                            </Link>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Box>
                        </Box>
                    ) : null}

                    {selectedApp.appId === APP_SITE_ID.SLIDING_PUZZLE_QUEST ? (
                        <Box sx={{ mt: 3, border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                                Ghost Guide asset
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL || ""}/v4/ghost-guide.png`}
                                    alt="Ghost Guide"
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        objectFit: "contain",
                                        bgcolor: "grey.100",
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: "divider",
                                    }}
                                />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.35 }}>
                                        PNG download (512×512)
                                    </Typography>
                                    <Link
                                        href={`${process.env.PUBLIC_URL || ""}/v4/ghost-guide.png`}
                                        download="ghost-guide.png"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Download ghost-guide.png
                                    </Link>
                                </Box>
                            </Stack>
                        </Box>
                    ) : null}

                    {selectedApp.appId === APP_SITE_ID.SLIDING_PUZZLE_QUEST
                    || selectedApp.appId === APP_SITE_ID.JIGSAW_MUSE ? (
                        <Box
                            sx={{ mt: 3, border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Product icons
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Master Pass, Pro Pack, Pocket Pack, Assistant packet, and Stripe
                                product images live under AD → Product Icon.
                            </Typography>
                            <Link
                                component={RouterLink}
                                to={ADMIN_ADVERTISEMENT_PRODUCT_ICON_PATH}
                                sx={{ fontWeight: 600 }}
                            >
                                Open Product Icon
                            </Link>
                        </Box>
                    ) : null}

                    {selectedApp.appId === WORD_SEARCH_MINI_APP_ID ? null : (
                        <PublicLinks appId={selectedApp.appId} />
                    )}
                </Box>
            </Box>
        </Box>
    );
}
